import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import PageHeader from "../../../../components/common/PageHeader";
import PageLoader from "../../../../components/common/PageLoader";
import StatusChip from "../../../../components/common/StatusChip";
import ChipList from "../../../../components/common/ChipList";
import { CLIENT_SCOPES } from "../../constants";
import { systemClientService, tenantClientService } from "../../services";
import { roleService } from "../../../user-management/services";
import { tenantRoleService } from "../../../role-management/services";
import { normalizeRoleList } from "../../../role-management/utils/normalizeRole";
import {
  formatClientDate,
  normalizeClient,
  normalizeClientList,
  pickClientSecret,
} from "../../utils/normalizeClient";
import ClientFormDialog from "../ClientFormDialog";
import UpdateClientRoleDialog from "../UpdateClientRoleDialog";
import { appActions } from "../../../../redux/actions";
import { getApiErrorMessage } from "../../../../helpers";
import { filterByCatalog } from "../../../../helpers/catalogFilter.helper";
import useAuth from "../../../../hooks/useAuth";

const DetailItem = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, mt: 0.25, wordBreak: "break-word" }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

const ClientDetailsPage = ({ scope = CLIENT_SCOPES.TENANT }) => {
  const isSystem = scope === CLIENT_SCOPES.SYSTEM;
  const { clientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { activeTenantId, tenantId: authTenantId, accessToken } = useAuth();
  const tenantId = activeTenantId || authTenantId;
  const listPath = isSystem ? "/clients" : "/tenant-clients";

  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [refreshOpen, setRefreshOpen] = useState(false);
  const [secretValue, setSecretValue] = useState("");
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [clientConfig, setClientConfig] = useState({
    allowSelfUserCreation: false,
    allowLoginViaOTP: false,
  });

  const loadClientBasicConfig = useCallback(async () => {
    if (isSystem || !clientId || !tenantId) return;
    try {
      const basic = await tenantClientService.getTenantClientBasic(
        clientId,
        tenantId
      );
      setClientConfig({
        allowSelfUserCreation: Boolean(basic?.allowSelfUserCreation),
        allowLoginViaOTP: Boolean(basic?.allowLoginViaOTP),
      });
    } catch {
      // Fall back to get-by-id values when /basic is unavailable.
      setClientConfig((prev) => prev);
    }
  }, [clientId, isSystem, tenantId]);

  const loadClient = useCallback(async ({ force = false, silent = false } = {}) => {
    if (!clientId) return;
    if (!silent) {
      setLoading(true);
      setError("");
    }
    try {
      let detail = null;
      if (isSystem) {
        const fromState = location.state?.client;
        if (
          !force &&
          fromState &&
          String(fromState.clientId || fromState.id) === String(clientId)
        ) {
          detail = fromState;
        } else {
          // No get-by-id API for system clients.
          const list = normalizeClientList(
            await systemClientService.getAllSystemClients()
          );
          detail =
            list.find(
              (item) =>
                String(item.clientId || item.id) === String(clientId)
            ) || null;
        }
        if (!detail) {
          throw new Error("Client not found.");
        }
      } else {
        detail = await tenantClientService.getTenantClientById(
          clientId,
          tenantId
        );
      }
      const normalized = normalizeClient(detail || {});
      setClient(normalized);
      if (!isSystem) {
        setClientConfig({
          allowSelfUserCreation: Boolean(normalized.allowSelfUserCreation),
          allowLoginViaOTP: Boolean(normalized.allowLoginViaOTP),
        });
        // Prefer /basic for Client Configuration checkboxes.
        await loadClientBasicConfig();
      }
    } catch (err) {
      if (!silent) {
        setClient(null);
        setError(getApiErrorMessage(err));
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [clientId, isSystem, loadClientBasicConfig, location.state, tenantId]);

  const handleSaveClientConfig = async () => {
    if (!client?.clientId || isSystem) return;
    setConfigSaving(true);
    try {
      await tenantClientService.updateTenantClient(
        client.clientId,
        {
          allowSelfUserCreation: clientConfig.allowSelfUserCreation,
          allowLoginViaOTP: clientConfig.allowLoginViaOTP,
        },
        tenantId,
        accessToken
      );
      dispatch(
        appActions.apiSuccessMessage("Client configuration updated successfully.")
      );
      await loadClient({ force: true, silent: true });
    } catch (err) {
      dispatch(appActions.apiErrorMessage(getApiErrorMessage(err)));
    } finally {
      setConfigSaving(false);
    }
  };

  useEffect(() => {
    loadClient();
  }, [loadClient]);

  const loadRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      if (isSystem) {
        setRoles(await roleService.getAllRoles());
      } else {
        const payload = await tenantRoleService.getAllTenantRoles(
          tenantId,
          accessToken
        );
        setRoles(
          normalizeRoleList(payload)
            .map((role) => String(role.name || "").trim())
            .filter(Boolean)
        );
      }
    } catch {
      setRoles([]);
    } finally {
      setRolesLoading(false);
    }
  }, [accessToken, isSystem, tenantId]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const visibleRoles = useMemo(
    () => filterByCatalog(client?.roles, roles),
    [client, roles]
  );

  const openEdit = async () => {
    // Load latest client (incl. redirectUri) before opening so the form is not wiped mid-edit.
    await loadClient({ force: true, silent: true });
    await loadRoles();
    setEditOpen(true);
  };

  const openRoleDialog = async () => {
    setRoleOpen(true);
    await loadRoles();
  };

  const handleUpdate = async (payload) => {
    if (!client?.clientId) return;
    setSaving(true);
    try {
      if (isSystem) {
        await systemClientService.updateSystemClient(client.clientId, payload);
        const nextRoles = Array.isArray(payload.roles) ? payload.roles : [];
        const prevRoles = Array.isArray(client.roles) ? client.roles : [];
        const rolesChanged =
          nextRoles.length !== prevRoles.length ||
          nextRoles.some((role) => !prevRoles.includes(role)) ||
          prevRoles.some((role) => !nextRoles.includes(role));
        if (rolesChanged) {
          await systemClientService.updateSystemClientRoles(
            client.clientId,
            nextRoles
          );
        }
      } else {
        await tenantClientService.updateTenantClient(
          client.clientId,
          payload,
          tenantId,
          accessToken
        );
        const nextRoles = Array.isArray(payload.roles) ? payload.roles : [];
        const prevRoles = Array.isArray(client.roles) ? client.roles : [];
        const rolesChanged =
          nextRoles.length !== prevRoles.length ||
          nextRoles.some((role) => !prevRoles.includes(role)) ||
          prevRoles.some((role) => !nextRoles.includes(role));
        if (rolesChanged) {
          await tenantClientService.updateTenantClientRoles(
            client.clientId,
            nextRoles,
            tenantId,
            accessToken
          );
        }
      }
      dispatch(appActions.apiSuccessMessage("Client updated successfully."));
      setEditOpen(false);
      await loadClient({ force: true });
    } catch (err) {
      throw new Error(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRoles = async (nextRoles) => {
    if (!client?.clientId) return;
    setSaving(true);
    try {
      if (isSystem) {
        await systemClientService.updateSystemClientRoles(
          client.clientId,
          nextRoles
        );
      } else {
        await tenantClientService.updateTenantClientRoles(
          client.clientId,
          nextRoles,
          tenantId,
          accessToken
        );
      }
      dispatch(appActions.apiSuccessMessage("Client roles updated successfully."));
      setRoleOpen(false);
      await loadClient({ force: true });
    } catch (err) {
      dispatch(appActions.apiErrorMessage(getApiErrorMessage(err)));
    } finally {
      setSaving(false);
    }
  };

  const handleRefreshSecret = async () => {
    if (!client?.clientId) return;
    setSaving(true);
    try {
      const result = isSystem
        ? await systemClientService.refreshSystemClientSecret(client.clientId)
        : await tenantClientService.refreshTenantClientSecret(
            client.clientId,
            tenantId,
            accessToken
          );
      const secret = pickClientSecret(result || {});
      setSecretValue(secret || "");
      dispatch(
        appActions.apiSuccessMessage("Client secret refreshed successfully.")
      );
      if (!secret) {
        setRefreshOpen(false);
      }
    } catch (err) {
      dispatch(appActions.apiErrorMessage(getApiErrorMessage(err)));
      setRefreshOpen(false);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageLoader text="Loading client..." />;
  }

  if (error && !client) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackOutlinedIcon />}
          onClick={() => navigate(listPath)}
        >
          Back to clients
        </Button>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!client) return null;

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        <Button
          startIcon={<ArrowBackOutlinedIcon />}
          onClick={() => navigate(listPath)}
        >
          Back to clients
        </Button>
      </Stack>
      <PageHeader
        title="Client Details"
        subtitle={client.name || client.clientId}
      />

      <Paper
        elevation={0}
        sx={{ p: 3, border: "1px solid", borderColor: "divider", mb: 2 }}
      >
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <DetailItem label="Client ID" value={client.clientId} />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailItem label="Client Name" value={client.name} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <StatusChip status={client.enabled ? "enabled" : "disabled"} />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Access options
                </Typography>
                <FormGroup sx={{ mt: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(client.allowUserCreation)}
                        disabled
                      />
                    }
                    label="Allow User Creation"
                  />
                </FormGroup>
              </Grid>
              {!isSystem ? (
                <Grid item xs={12}>
                  <DetailItem
                    label="Privacy Policy"
                    value={client.privacyPolicy}
                  />
                </Grid>
              ) : null}
              <Grid item xs={12} sm={6}>
                <DetailItem
                  label="Updated Date"
                  value={formatClientDate(
                    client.lastModifiedDate || client.updatedAt
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem
                  label="Access token expiry (sec)"
                  value={client.accessTokenExpiryInSecs}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem
                  label="Refresh token expiry (sec)"
                  value={
                    client.supportsRefreshToken
                      ? client.refreshTokenExpiryInSecs
                      : "—"
                  }
                />
              </Grid>
            </Grid>
          </Box>

          {!isSystem ? (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                  Client Configuration
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(clientConfig.allowSelfUserCreation)}
                        onChange={(event) =>
                          setClientConfig((prev) => ({
                            ...prev,
                            allowSelfUserCreation: event.target.checked,
                          }))
                        }
                      />
                    }
                    label="Allow Self User Registration"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(clientConfig.allowLoginViaOTP)}
                        onChange={(event) =>
                          setClientConfig((prev) => ({
                            ...prev,
                            allowLoginViaOTP: event.target.checked,
                          }))
                        }
                      />
                    }
                    label="Allow Login via OTP"
                  />
                </FormGroup>
                <Button
                  variant="contained"
                  size="small"
                  sx={{ mt: 1.5 }}
                  disabled={configSaving}
                  onClick={handleSaveClientConfig}
                >
                  {configSaving ? "Saving..." : "Save Configuration"}
                </Button>
              </Box>
            </>
          ) : null}

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Grant
            </Typography>
            <ChipList
              items={client.grantType}
              maxVisible={null}
              emptyLabel="No grant types"
              sx={{ flexWrap: "wrap", rowGap: 0.75, overflow: "visible" }}
            />
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Roles
            </Typography>
            <ChipList
              items={visibleRoles}
              maxVisible={null}
              emptyLabel="No roles assigned"
              sx={{ flexWrap: "wrap", rowGap: 0.75, overflow: "visible" }}
            />
          </Box>

          {!isSystem ? (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                  Redirect URLs
                </Typography>
                <ChipList
                  items={client.redirectUri}
                  maxVisible={null}
                  emptyLabel="No redirect URLs"
                  sx={{ flexWrap: "wrap", rowGap: 0.75, overflow: "visible" }}
                />
              </Box>
            </>
          ) : null}
        </Stack>
      </Paper>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.25}
        flexWrap="wrap"
        useFlexGap
      >
        <Button variant="contained" onClick={openEdit}>
          Edit Client
        </Button>
        <Button variant="outlined" onClick={openRoleDialog}>
          Update Roles
        </Button>
        <Button
          variant="outlined"
          color="warning"
          onClick={() => {
            setSecretValue("");
            setRefreshOpen(true);
          }}
        >
          Refresh Client Secret
        </Button>
      </Stack>

      <ClientFormDialog
        open={editOpen}
        mode="edit"
        client={client}
        roles={roles}
        rolesLoading={rolesLoading}
        loading={saving}
        showExtendedFields={!isSystem}
        onClose={() => setEditOpen(false)}
        onSubmit={handleUpdate}
      />

      <UpdateClientRoleDialog
        open={roleOpen}
        client={client}
        roles={roles}
        loading={saving}
        rolesLoading={rolesLoading}
        onClose={() => setRoleOpen(false)}
        onSubmit={handleUpdateRoles}
      />

      <Dialog
        open={refreshOpen}
        onClose={saving ? undefined : () => setRefreshOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Refresh Client Secret</DialogTitle>
        <DialogContent>
          {secretValue ? (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              Copy this secret now. It may not be shown again.
            </Alert>
          ) : (
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Refresh secret for{" "}
              <strong>{client.name || client.clientId}</strong>?
            </Typography>
          )}
          {secretValue ? (
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                wordBreak: "break-all",
                p: 1.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "action.hover",
              }}
            >
              {secretValue}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRefreshOpen(false)} disabled={saving}>
            {secretValue ? "Close" : "Cancel"}
          </Button>
          {!secretValue ? (
            <Button
              variant="contained"
              color="warning"
              onClick={handleRefreshSecret}
              disabled={saving}
            >
              {saving ? "Refreshing..." : "Refresh Secret"}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientDetailsPage;
